/**
 * OmegaCockpitPage — OMEGA founder command center.
 *
 * Single screen showing all 14 live status tiles across 4 rows:
 *   OMEGA STATUS · REVENUE · OPERATIONS · INFRASTRUCTURE
 *
 * All queries fire in parallel on mount (useQueries) and refresh every 20s
 * in both foreground and background — no manual refresh ever needed.
 *
 * OmegaSidebar is provided by OmegaLayoutShell in App.tsx — not rendered here.
 */

import { Navigate, Link } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { isOmegaAdmin, OMEGA_ADMIN_STORAGE_KEY } from '@/features/omega/adminMode';
import { apiFetch } from '@/api/http';
import { useIsMobile } from '@/features/omega/cockpit/OmegaSidebar';
import { StatusCard } from '@/features/omega/cockpit/StatusCard';
import {
  monitorCard,
  loopCard,
  dlmCard,
  decompositionCard,
  discoveryCard,
  researchCard,
  knowledgeGatesCard,
  businessCard,
  budgetCard,
  type CardState,
} from '@/features/omega/cockpit/cardStatus';

function consumeFounderUnlockOnce(): void {
  if (typeof window === 'undefined') return;
  try {
    if (new URLSearchParams(window.location.search).get('key') === 'founder') {
      window.localStorage.setItem(OMEGA_ADMIN_STORAGE_KEY, 'true');
    }
  } catch {
    // localStorage unavailable
  }
}

// ── Tile definitions (order determines useQueries result index) ───────────────

interface TileDef {
  key: string;
  path: string;
  title: string;
  to: string;
  cardFn: (data: unknown, isError: boolean) => CardState;
}

const TILES: TileDef[] = [
  // OMEGA STATUS row (indices 0–3)
  { key: 'monitor',       path: '/admin/fingerprints',              title: 'Health',          to: '/omega/monitor',        cardFn: monitorCard },
  { key: 'loop',          path: '/admin/brain/loop/status',         title: 'Loop',            to: '/omega/loop',           cardFn: loopCard },
  { key: 'dlm',           path: '/omega/dlm/summary',               title: 'DLM',             to: '/omega/dlm',            cardFn: dlmCard },
  { key: 'gates',         path: '/admin/learning/gates',            title: 'Knowledge Gates', to: '/omega/learning/gates', cardFn: knowledgeGatesCard },
  // REVENUE row (indices 4–6)
  { key: 'products',      path: '/omega/business/products/summary', title: 'Products',        to: '/omega/products',       cardFn: businessCard },
  { key: 'sales',         path: '/omega/business/sales/summary',    title: 'Sales',           to: '/omega/sales',          cardFn: businessCard },
  { key: 'pipeline',      path: '/omega/business/pipeline/summary', title: 'Pipeline',        to: '/omega/sales/pipeline', cardFn: businessCard },
  // OPERATIONS row (indices 7–10)
  { key: 'discovery',     path: '/omega/brain/discovery/outputs',   title: 'Discovery Labs',  to: '/omega/brain/discovery',  cardFn: discoveryCard },
  { key: 'research',      path: '/admin/research/conductors',       title: 'Research Teams',  to: '/omega/research/teams',   cardFn: researchCard },
  { key: 'decomposition', path: '/admin/decomposition/summary',     title: 'Decomposition',   to: '/omega/decomposition',    cardFn: decompositionCard },
  { key: 'marketing',     path: '/omega/business/marketing/summary', title: 'Marketing',      to: '/omega/marketing',        cardFn: businessCard },
  // INFRASTRUCTURE row (indices 11–13)
  { key: 'customers',     path: '/omega/business/customers/summary', title: 'Customers',      to: '/omega/customers',        cardFn: businessCard },
  { key: 'service',       path: '/omega/business/service/summary',  title: 'Service',         to: '/omega/customers/support', cardFn: businessCard },
  { key: 'budget',        path: '/admin/budget/summary',            title: 'Budget',          to: '/omega/budget',           cardFn: budgetCard },
];

const REFETCH_MS = 20_000;

// ── Layout helpers ────────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 28 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: '#6b7280',
          marginBottom: 10,
        }}
      >
        {title}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 240px), 1fr))',
          gap: 12,
        }}
      >
        {children}
      </div>
    </section>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function OmegaCockpitPage() {
  consumeFounderUnlockOnce();
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const isMobile = useIsMobile();

  // All 14 tile queries fire in parallel on mount.
  const results = useQueries({
    queries: TILES.map((t) => ({
      queryKey: ['cockpit', t.key],
      queryFn: () => apiFetch<unknown>(t.path),
      refetchInterval: REFETCH_MS,
      refetchIntervalInBackground: true,
      retry: false,
    })),
  });

  const tile = (index: number) => {
    const t = TILES[index]!;
    const r = results[index]!;
    return (
      <StatusCard
        key={t.key}
        title={t.title}
        to={t.to}
        state={t.cardFn(r.data, r.isError)}
        loading={r.isPending}
      />
    );
  };

  return (
    <div style={{ background: '#f9fafb', minHeight: '100vh', color: '#111827' }}>
      <div
        style={{
          maxWidth: 1100,
          margin: '0 auto',
          padding: isMobile ? '56px 16px 80px' : '20px 24px 80px',
        }}
      >
        {/* Header with Kill Switch */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginBottom: 24,
            gap: 12,
          }}
        >
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>OMEGA</h1>
            <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 13 }}>
              Your company at a glance. Green is healthy, yellow needs attention, red needs action.
            </p>
          </div>
          <Link
            to="/omega/kill-switch"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 14px',
              border: '1px solid #dc2626',
              color: '#dc2626',
              borderRadius: 4,
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textDecoration: 'none',
              textTransform: 'uppercase',
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
          >
            ⚠ Kill Switch
          </Link>
        </div>

        {/* Row 1: OMEGA STATUS */}
        <Section title="OMEGA Status">
          {tile(0)}{tile(1)}{tile(2)}{tile(3)}
        </Section>

        {/* Row 2: REVENUE */}
        <Section title="Revenue">
          {tile(4)}{tile(5)}{tile(6)}
        </Section>

        {/* Row 3: OPERATIONS */}
        <Section title="Operations">
          {tile(7)}{tile(8)}{tile(9)}{tile(10)}
        </Section>

        {/* Row 4: INFRASTRUCTURE */}
        <Section title="Infrastructure">
          {tile(11)}{tile(12)}{tile(13)}
        </Section>
      </div>

      {/* Floating Spark button */}
      <Link
        to="/spark"
        aria-label="Spark"
        style={{
          position: 'fixed',
          right: 20,
          bottom: 20,
          background: '#111827',
          color: '#ffffff',
          borderRadius: 999,
          padding: '12px 18px',
          fontSize: 14,
          fontWeight: 600,
          textDecoration: 'none',
          boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
        }}
      >
        ✦ Spark
      </Link>
    </div>
  );
}
